import { call, put, takeLatest, select } from 'redux-saga/effects'
import { GET_PRODUCTS } from '../App/constants'
import { getProductsSuccess, getProductsError } from '../App/actions'
import request from '../../utils/request'

const serverPathTgdApi = process.env.REACT_APP_TGDAPI_SERVER || ''

const COLOR_TO_CATEGORY = {
  '#6462FC': 'Eval',  '#1AA0FF': 'Eval',
  '#3494FA': 'Conduite', '#166DD8': 'Conduite', '#0CBCB5': 'Conduite',
  '#4CAF50': 'Conduite', '#F59E0B': 'Conduite', '#3B82F6': 'Conduite',
  '#7EDBE3': 'Hardware', '#A78BFA': 'Hardware', '#00C8F8': 'Hardware',
}

const getShortDescription = (text) => {
  if (!text) return ''
  const first = text.split('\n')[0].trim()
  return first.length > 200 ? first.substring(0, 200).trim() + '…' : first
}

const normalizeProgramme = (modulesContent) => {
  if (!Array.isArray(modulesContent)) return []
  return modulesContent.map((m, i) => ({
    id: m.id || i + 1,
    title: m.title || '',
    type: m.type || '',
    duration: m.duration || '',
    description: m.description || '',
  }))
}

// Legacy sequence modules had no details: the module itself becomes its single detail
const toSequenceModule = (m) => (Array.isArray(m.details)
  ? m
  : { title: m.title, duration: m.duration, description: '', image: null, details: [m] })

const normalizeDetails = (details, moduleNum) => (Array.isArray(details) ? details : [])
  .filter(Boolean)
  .map((d, i) => ({
    id: `${moduleNum}.${i + 1}`,
    title: d.title || '',
    type: d.type || '',
    duration: d.duration || '',
    description: d.description || '',
  }))

const getSequenceModules = (s) => ((s && Array.isArray(s.modulesContent)) ? s.modulesContent : [])
  .filter(Boolean)
  .map(toSequenceModule)

const parseMinutes = (text) => {
  const match = String(text || '').trim().match(/^(\d+(?:[.,]\d+)?)\s*(h|heures?|min|minutes?)$/i)
  if (!match) return null
  const value = parseFloat(match[1].replace(',', '.'))
  return /^h/i.test(match[2]) ? value * 60 : value
}

const formatMinutes = (total) => {
  const hours = Math.floor(total / 60)
  const minutes = Math.round(total % 60)
  if (!hours) return `${minutes} minutes`
  if (!minutes) return `${hours} heure${hours > 1 ? 's' : ''}`
  return `${hours} h ${String(minutes).padStart(2, '0')}`
}

// Empty when any duration is free text that cannot be summed
const sumDurations = (items) => {
  if (!items.length) return ''
  let total = 0
  for (const item of items) {
    const minutes = parseMinutes(item.duration)
    if (minutes == null) return ''
    total += minutes
  }
  return formatMinutes(total)
}

// URLs signées par tgd-api (le navigateur n'a jamais les clés AWS)
const getMediaUrls = (p) => ({
  icon: (p.mediaUrls && p.mediaUrls.icon) || null,
  files: (p.mediaUrls && p.mediaUrls.files) || {},
})

const normalizeSequences = (sequencesContent, files = {}) => {
  if (!Array.isArray(sequencesContent)) return []
  let offset = 0
  return sequencesContent.filter(Boolean).map((s) => {
    // Module numbering continues across sequences
    const modules = getSequenceModules(s).map((m, i) => {
      const num = offset + i + 1
      return {
        id: num,
        title: m.title || '',
        duration: m.duration || '',
        description: m.description || '',
        image: m.image || null,
        imageUrl: (m.image && files[m.image]) || null,
        details: normalizeDetails(m.details, num),
      }
    })
    offset += modules.length
    return {
      title: s.title || '',
      description: s.description || '',
      duration: s.duration || sumDurations(modules),
      modules,
    }
  })
}

const getProductModules = (p) => {
  if (Array.isArray(p.sequencesContent) && p.sequencesContent.length) {
    return p.sequencesContent.reduce(
      (acc, s) => acc.concat(getSequenceModules(s).reduce((a, m) => a.concat(m.details || []), [])),
      []
    )
  }
  return p.modulesContent
}

const getProductImages = (p) => {
  if (Array.isArray(p.medias) && p.medias.length) {
    return p.medias.map((m) => m && m.file).filter(Boolean)
  }
  return Array.isArray(p.images) ? p.images : []
}

const extractTrainingId = (product) => {
  if (!product || typeof product !== 'object') return null
  const direct = product.trainingId ?? product.training_id
  if (direct !== undefined && direct !== null && String(direct) !== '') return String(direct)

  const training = product.training
  if (training && typeof training === 'object') {
    const nested = training.id ?? training._id
    if (nested !== undefined && nested !== null && String(nested) !== '') return String(nested)
  }

  const trainings = Array.isArray(product.trainings) ? product.trainings : []
  if (trainings.length > 0) {
    const first = trainings[0]
    if (typeof first === 'string' || typeof first === 'number') return String(first)
    if (first && typeof first === 'object') {
      const nested = first.id ?? first._id
      if (nested !== undefined && nested !== null && String(nested) !== '') return String(nested)
    }
  }

  return null
}

// Projet de vente de ce build : clé des offres produit (ECF, PPsimu)
const salesProject = process.env.REACT_APP_PROJECT || ''

// Même règle que tgd-api (catalog-offer.ts) : CER_xxx -> CER, ML_xxx -> ML
export const getPriceGroup = (agencyName) => {
  const match = /^(CER|ML)_/i.exec(String(agencyName || ''))
  return match ? match[1].toUpperCase() : null
}

// Même règle que tgd-api (catalog-offer.ts) : l'offre du groupe prime sur l'offre standard,
// null si le produit n'est pas vendu sur ce projet
export const resolveOffer = (p, project = salesProject, priceGroup = null) => {
  if (!project || !Array.isArray(p.offers)) return null
  const active = p.offers.filter((o) => o && o.project === project && o.active !== false)
  const offer = (priceGroup && active.find((o) => o.priceGroup === priceGroup)) ||
    active.find((o) => !o.priceGroup)
  if (!offer || !Number.isInteger(offer.price) || offer.price < 1) return null
  return { price: offer.price / 100, sku: String(offer.sku || '').trim() }
}

// Groupes tarifaires par projet, alignés sur PRICE_GROUPS de what-api
const PRICE_GROUPS = { PPsimu: ['CER', 'ML'] }

const hasGroupOffer = (p, group) => Array.isArray(p.offers) && p.offers.some((o) =>
  o && o.project === salesProject && o.priceGroup === group && o.active !== false)

// Prix effectif de chaque groupe (null si non vendu à ce groupe), specific = prix propre au groupe
export const getGroupPrices = (p) => (PRICE_GROUPS[salesProject] || []).map((group) => {
  const offer = resolveOffer(p, salesProject, group)
  return { group, price: offer ? offer.price : null, specific: hasGroupOffer(p, group) }
})

const fmtPrice = (n) => n.toFixed(2).replace('.', ',') + ' €'

// "CER_ 9,99 € · ML_ 12,99 € (standard)"
export const describeGroupPrices = (groupPrices) => (groupPrices || [])
  .map(({ group, price, specific }) => {
    if (price == null) return `${group}_ non vendu`
    return `${group}_ ${fmtPrice(price)}${specific ? '' : ' (standard)'}`
  })
  .join(' · ')

export const normalizeProduct = (p, offer) => {
  const mediaUrls = getMediaUrls(p)
  const images = getProductImages(p)
  return {
    id: String(p.id ?? p._id ?? ''),
    sku: offer ? offer.sku : '',
    trainingId: extractTrainingId(p),
    title: p.title || '',
    color: p.color || '#3494FA',
    price: offer ? offer.price : 0,
    duration: p.duration || '',
    description: p.globalDescription || '',
    shortDescription: getShortDescription(p.globalDescription),
    category: p.category || COLOR_TO_CATEGORY[p.color] || 'Produit',
    badge: null,
    videoUrl: null,
    icon: p.icon || null,
    iconUrl: mediaUrls.icon,
    programme: normalizeProgramme(getProductModules(p)),
    sequences: normalizeSequences(p.sequencesContent, mediaUrls.files),
    content: Array.isArray(p.content) ? p.content : [],
    images,
    imageUrls: images.map((f) => mediaUrls.files[f] || null),
  }
}

export function * fetchProducts () {
  const globalState = yield select((state) => state.get('global'))
  const isAdmin = globalState.get('isAdmin')
  const priceGroup = getPriceGroup(globalState.get('agencyName'))
  const agencyToken = globalState.get('agencytoken')

  if (!agencyToken) {
    yield put(getProductsSuccess([]))
    return
  }

  try {
    // tgd-api filtre selon les produits autorisés de l'agence et signe les URLs des médias
    const data = yield call(request, `${serverPathTgdApi}/catalog/products`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${agencyToken}` },
    })
    const list = Array.isArray(data) ? data : []

    // Seuls les produits avec une offre sur ce projet sont proposés
    const forSale = list.reduce((acc, p) => {
      const offer = resolveOffer(p, salesProject, priceGroup)
      if (!isAdmin) return offer ? acc.concat(normalizeProduct(p, offer)) : acc
      // Superadmin : prix de chaque groupe tarifaire, y compris les produits réservés à un groupe
      const groupPrices = getGroupPrices(p)
      if (!offer && !groupPrices.some((g) => g.price != null)) return acc
      return acc.concat({ ...normalizeProduct(p, offer), price: offer ? offer.price : null, groupPrices })
    }, [])
    yield put(getProductsSuccess(forSale))
  } catch (err) {
    yield put(getProductsError(err))
  }
}

export function * getProductsSaga () {
  yield takeLatest(GET_PRODUCTS, fetchProducts)
}
